import React, { useMemo, useState, useEffect, useRef } from 'react';
import Plot from 'react-plotly.js';
import { Loader } from '../components/Components';
import './gene.css';
import { FixedSizeList as List } from 'react-window';

// Searchable Select Component - Optimized for performance
const SearchableSelect = React.memo(({ options = [], value, onChange, placeholder, label }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    // Use useMemo instead of useEffect + state for filtering to avoid extra renders
    const filteredOptions = useMemo(() => {
        if (!searchTerm) return options;
        return options.filter(option =>
            option.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [searchTerm, options]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.searchable-select')) {
                setIsOpen(false);
                setSearchTerm('');
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [isOpen]);

    const handleSelect = (option) => {
        onChange(option);
        setIsOpen(false);
        setSearchTerm('');
    };

    return (
        <div className="searchable-select">
<div className="searchable-select-input" onClick={() => setIsOpen(true)}>
                <input
                    type="text"
                    value={isOpen ? searchTerm : value || ''}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={placeholder}
                    onFocus={() => setIsOpen(true)}
                    className="search-input"
                />
                <svg className="dropdown-icon" width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            </div>

            {isOpen && (
                <div className="searchable-select-dropdown">
                    <div className="dropdown-content">
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((option, index) => (
                                <div
                                    key={index}
                                    className={`dropdown-option ${option === value ? 'selected' : ''}`}
                                    onClick={() => handleSelect(option)}
                                >
                                    {option}
                                </div>
                            ))
                        ) : (
                            <div className="dropdown-no-results">No results found</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
});
const ExpressionBoxPlot = ({ data, colors, geneName }) => {
    // --- FIX: Call hooks unconditionally at the top ---
    const plotData = useMemo(() => {
        // The logic inside the hook can handle empty data gracefully.
        if (!data || data.length === 0) {
            console.log('🚫 ExpressionBoxPlot: No data available');
            return [];
        }

  
        // Check color matching
        const colorMatches = data.map(d => {
            const disease = d.disease;
            const color = colors[disease];
            return { disease, color: color || '#cccccc', found: !!color };
        });


        // Group data by disease and create separate traces for proper coloring
        const diseaseGroups = {};
        data.forEach(d => {
            if (!diseaseGroups[d.disease]) {
                diseaseGroups[d.disease] = [];
            }
            diseaseGroups[d.disease].push(d.tpm);
        });


        // Create separate box plot trace for each disease (like MetabolicPage)
        return Object.entries(diseaseGroups).map(([disease, values]) => ({
            y: values,
            x: Array(values.length).fill(disease),
            type: 'box',
            name: disease,
            boxpoints: 'all',
            jitter: 0.3,
            pointpos: 0,
            fillcolor: 'rgba(0,0,0,0)',
            line: { color: 'rgba(0,0,0,0.3)', width: 1 },
            marker: {
                color: colors[disease] || '#cccccc',
                size: 4,
                opacity: 0.7
            },
            hovertemplate: `<b>${disease}</b><br>TPM: %{y:.3f}<extra></extra>`,
            showlegend: false
        }));
    }, [data, colors]);

    const plotLayout = useMemo(() => ({
        title: {
            text: `Expression (TPM) for <b>${geneName}</b>`,
            font: { size: 14, color: '#4b5563' },
            x: 0.5,
            y: 0.95
        },
        yaxis: {
            title: 'Expression (TPM)',
            titlefont: { size: 12 },
            gridcolor: 'rgba(0,0,0,0.05)',
            zeroline: false
        },
        xaxis: {
            tickangle: 45,
            tickfont: { size: 10 }
        },
        font: { family: '-apple-system, BlinkMacSystemFont, Segoe UI, sans-serif', size: 11 },
        paper_bgcolor: 'rgba(0,0,0,0)',
        plot_bgcolor: 'rgba(248, 250, 252, 0.0)',
        margin: { l: 60, r: 20, t: 40, b: 120 },
        showlegend: false,
    }), [geneName]);
    
    // --- FIX: Now that hooks have been called, we can conditionally render ---
    if (!data || data.length === 0) {
        return (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#9ca3af' }}>
                No expression data available for this plot.
            </div>
        );
    }
    
    return (
        <Plot
            data={plotData}
            layout={plotLayout}
            config={{ displayModeBar: false, responsive: true }}
            style={{ width: '100%', height: '100%' }}
        />
    );
};
// Clean DataTable component
const CleanDataTable = ({ data, title }) => {
    if (!data || data.length === 0) {
        return (
            <div className="gene-glass-card">
                <div className="gene-card-title">
                    {title}
                    <span className="gene-table-count">0 items</span>
                </div>
                <div style={{ textAlign: 'center', padding: '2rem', color: '#9ca3af' }}>
                    No data available
                </div>
            </div>
        );
    }
    
    const headers = Object.keys(data[0]);
    
    return (
        <div className="gene-glass-card">
            <div className="gene-card-title">
                {title}
                <span className="gene-table-count">{data.length} items</span>
            </div>
            <div className="gene-table-wrapper">
                <table className="gene-enhanced-table">
                    <thead>
                        <tr>
                            {headers.map(header => (
                                <th key={header}>{header}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((row, index) => (
                            <tr key={index}>
                                {headers.map(header => (
                                    <td key={header}>
                                        {typeof row[header] === 'number' 
                                            ? Number(row[header]).toFixed(4) 
                                            : String(row[header] || '')
                                        }
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

function GenePage({ selections, handleSelection, options, data, isTabLoading, diseaseColors }) {
    // SIMPLE: Just use the parent state directly for button highlighting
    // No local state, no syncing, no complexity
    const currentMode = selections.geneAnalysisMode || 'gene';

    // Handle mode switching - reset to first option to mimic "first time" behavior
    const handleModeChange = (mode) => {
        handleSelection('geneAnalysisMode', mode);

        // Reset to first option to always feel like first time
        if (mode === 'gene') {
            handleSelection('gene', options.genes?.[0] || ''); // Reset to first gene
        } else {
            handleSelection('disease', options.diseases?.[0] || ''); // Reset to first disease
        }
    };

    // Check if we have the required data for the current mode
    const hasRequiredData = useMemo(() => {
        if (currentMode === 'gene') {
            return !!(data?.geneTpm && data?.geneDeg && data?.geneMarkers);
        } else {
            return !!(data?.diseaseVolcano && data?.diseaseUp && data?.diseaseDown);
        }
    }, [data, currentMode]);

    // Simple loading logic
    const shouldShowLoading = isTabLoading || !hasRequiredData;

    // Sorted data for better visualization - only sort data for current mode to improve performance
    const sortedData = useMemo(() => {
        if (!data) return {};

        const result = {};

        // Only process data for the current mode to improve performance
        if (currentMode === 'gene') {
            result.geneTpm = data.geneTpm ? [...data.geneTpm].sort((a, b) => (b.mean_tpm || 0) - (a.mean_tpm || 0)) : [];
            result.geneDeg = data.geneDeg ? [...data.geneDeg]
                .filter(d => d.padj < 0.05)
                .sort((a, b) => {
                    const aVal = a.log2FoldChange || 0;
                    const bVal = b.log2FoldChange || 0;

                    // First sort by positive/negative
                    if (aVal >= 0 && bVal < 0) return -1; // positive first
                    if (aVal < 0 && bVal >= 0) return 1;  // negative second

                    // For positive values: longest first (descending)
                    if (aVal >= 0 && bVal >= 0) return bVal - aVal;

                    // For negative values: shortest first (ascending, closest to zero first)
                    if (aVal < 0 && bVal < 0) return bVal - aVal;

                    return 0;
                }) : [];
            result.geneMarkers = data.geneMarkers ? [...data.geneMarkers] : [];
        } else {
            result.diseaseVolcano = data.diseaseVolcano ? [...data.diseaseVolcano]
                .sort((a, b) => (a.padj || 1) - (b.padj || 1)) : [];
            result.diseaseUp = data.diseaseUp ? [...data.diseaseUp]
                .sort((a, b) => (b.log2FoldChange || 0) - (a.log2FoldChange || 0)) : [];
            result.diseaseDown = data.diseaseDown ? [...data.diseaseDown]
                .sort((a, b) => (a.log2FoldChange || 0) - (b.log2FoldChange || 0)) : [];
        }

        return result;
    }, [data, currentMode]);

    // Clean plot configuration
    const plotConfig = {
        displayModeBar: false,
        responsive: true
    };

    const plotLayout = {
        font: { family: '-apple-system, BlinkMacSystemFont, Segoe UI, sans-serif', size: 11 },
        paper_bgcolor: 'rgba(0,0,0,0)',
        plot_bgcolor: 'rgba(248, 250, 252, 0.5)',
        margin: { l: 50, r: 20, t: 20, b: 80 },
        showlegend: false
    };

    // Create box plot data for gene expression across conditions
    const createConditionBoxPlotData = (geneTpmData, selectedGene) => {
        console.log('🔍 createConditionBoxPlotData called with:');
        console.log('  - geneTpmData:', geneTpmData ? `Array of ${geneTpmData.length} items` : 'null/undefined');
        console.log('  - selectedGene:', selectedGene);
        console.log('  - diseaseColors:', diseaseColors);

        if (!geneTpmData || !selectedGene) {
            console.log('❌ Missing data or gene, returning empty array');
            return [];
        }

        // Log first few items to see data structure
        console.log('📊 First 3 geneTpmData items:', geneTpmData.slice(0, 3));
        console.log('🔍 Available keys in first item:', Object.keys(geneTpmData[0] || {}));

        // The data structure shows it's already filtered for the selected gene
        // and contains disease-level aggregated data, not individual gene records
        const geneData = geneTpmData; // Use all data since it's already for the selected gene
        console.log(`🎯 Using all data (already filtered for gene "${selectedGene}"):`, geneData.length, 'items');

        if (!geneData || geneData.length === 0) {
            console.log('❌ No data found for selected gene, returning empty array');
            return [];
        }

        // Log first few filtered items
        console.log('📋 First 3 filtered items:', geneData.slice(0, 3));

        // Group by disease and create box plot traces
        const diseaseGroups = {};
        geneData.forEach(d => {
            const disease = d.disease; // Use 'disease' field instead of 'disease_name_level2'
            if (!diseaseGroups[disease]) {
                diseaseGroups[disease] = [];
            }
            diseaseGroups[disease].push(d.mean_tpm);
        });

        console.log('🏥 Disease groups:', Object.keys(diseaseGroups));
        console.log('📈 Disease group sizes:', Object.entries(diseaseGroups).map(([k, v]) => `${k}: ${v.length}`));

        // Since we have aggregated data (one value per disease), create a bar plot instead of box plot
        const plotData = [{
            x: Object.keys(diseaseGroups),
            y: Object.values(diseaseGroups).map(values => values[0]), // Take the single value for each disease
            type: 'bar',
            marker: {
                color: Object.keys(diseaseGroups).map(disease => diseaseColors[disease] || '#cccccc'),
                opacity: 0.8,
                line: {
                    color: Object.keys(diseaseGroups).map(disease => diseaseColors[disease] || '#cccccc'),
                    width: 1
                }
            },
            hovertemplate: '%{x}<br>Expression (TPM): %{y:.3f}<extra></extra>'
        }];

        console.log('📊 Final plot data:', plotData.length, 'traces');
        console.log('🎨 Plot data structure:', plotData);

        return plotData;
    };

    return (
        <div className="gene-page">
            {/* Sidebar */}
            <div className="gene-sidebar">
                <h1 className="gene-sidebar-title">Gene Level Analysis</h1>
                
                <div className="gene-sidebar-section">
                    <p className="gene-sidebar-label">Analysis Mode</p>
                    <div className="gene-mode-controls">
                        <button
                            className={`gene-mode-btn ${currentMode === 'gene' ? 'active' : ''}`}
                            onClick={() => handleModeChange('gene')}
                        >
                            Gene Specific
                        </button>
                        <button
                            className={`gene-mode-btn ${currentMode === 'disease' ? 'active' : ''}`}
                            onClick={() => handleModeChange('disease')}
                        >
                            Disease Specific
                        </button>
                    </div>
                </div>

                <div className="gene-sidebar-section">
                    <p className="gene-sidebar-label">
                        {currentMode === 'gene' ? 'Select Gene' : 'Select Disease'}
                    </p>
                    <SearchableSelect
                        key={currentMode} // Force re-mount when mode changes to avoid state conflicts
                        options={currentMode === 'gene' ? (options.genes || []) : (options.diseases || [])}
                        value={currentMode === 'gene' ? selections.gene : selections.disease}
                        onChange={(value) => handleSelection(
                            currentMode === 'gene' ? 'gene' : 'disease',
                            value
                        )}
                        placeholder={`Search ${currentMode === 'gene' ? 'genes' : 'diseases'}...`}
                    />
                </div>
            </div>

            {/* Main Content */}
            <div className="gene-main-content">
                {/* Loading State */}
                {shouldShowLoading && (
                    <div className="gene-loading">
                        <div className="gene-loading-spinner"></div>
                        Loading analysis data...
                    </div>
                )}

                {/* Gene Mode */}
                {!shouldShowLoading && currentMode === 'gene' && (
                    <div className="gene-content">
                        {/* Full Width Sections */}

                        {/* 1. Expression Levels - Full Width */}
                        <div className="gene-full-width-section">
                            <div className="gene-glass-card">
                                <div className="gene-card-title">
                                    Expression Levels
                                    <span className="gene-tag">{selections.gene}</span>
                                </div>

<div className="gene-plot-container" style={{ height: '450px' }}>
    <ExpressionBoxPlot 
        data={data.geneExpressionBoxplot} 
        colors={diseaseColors}
        geneName={selections.gene}
    />
</div>
                            </div>
                        </div>

                        {/* 2. Expression Data Table - Full Width */}
                        <div className="gene-full-width-section">
                            <CleanDataTable data={sortedData.geneTpm} title="Expression Data" />
                        </div>

                        {/* 3. Differential Expression Graph - Full Width */}
                        <div className="gene-full-width-section">
                            <div className="gene-glass-card">
                                <div className="gene-card-title">
                                    Differential Expression
                                    <span className="gene-tag">p-adj &lt; 0.05</span>
                                </div>
                                <p className="gene-card-subtitle">Log2 fold changes for significant results</p>

                                <div className="gene-plot-container">
                                    <Plot
                                        data={[{
                                            y: sortedData.geneDeg?.map(d => d.disease),
                                            x: sortedData.geneDeg?.map(d => d.log2FoldChange),
                                            type: 'bar',
                                            orientation: 'h',
                                            marker: {
                                                color: sortedData.geneDeg?.map(d =>
                                                    d.log2FoldChange > 0 ? '#fca5a5' : '#7dd3fc'
                                                ),
                                                opacity: 0.8,
                                                line: {
                                                    color: sortedData.geneDeg?.map(d =>
                                                        d.log2FoldChange > 0 ? '#ef4444' : '#0ea5e9'
                                                    ),
                                                    width: 1
                                                },
                                                cornerradius: 20
                                            },
                                            hovertemplate: '<b>%{y}</b><br>Log2FC: %{x:.3f}<extra></extra>'
                                        }]}
                                        layout={{
                                            ...plotLayout,
                                            height: 280,
                                            xaxis: {
                                                title: 'Log2 Fold Change',
                                                titlefont: { size: 12 },
                                                zeroline: true,
                                                zerolinecolor: 'rgba(0,0,0,0.3)',
                                                zerolinewidth: 1,
                                                showgrid: true,
                                                gridcolor: 'rgba(0,0,0,0.05)'
                                            },
                                            yaxis: {
                                                tickfont: { size: 10 },
                                                showgrid: false
                                            }
                                        }}
                                        config={plotConfig}
                                        style={{ width: '100%', height: '280px' }}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* 4. Differential Expression Data - Full Width */}
                        <div className="gene-full-width-section">
                            <CleanDataTable data={sortedData.geneDeg} title="Differential Expression" />
                        </div>

                        {/* 5. HPA Disease Specificity - Full Width */}
                        <div className="gene-full-width-section">
                            <CleanDataTable data={sortedData.geneMarkers} title="HPA Disease Specificity" />
                        </div>
                    </div>
                )}

                {/* Disease Mode */}
                {!shouldShowLoading && currentMode === 'disease' && (
                    <div className="gene-content">
                        <div className="disease-mode-grid">
                            {/* Volcano Plot */}
                            <div className="gene-volcano-section">
                                <div className="gene-glass-card">
                                    <div className="gene-card-title">
                                        Volcano Plot
                                        <span className="gene-tag">{selections.disease}</span>
                                    </div>
                                    <p className="gene-card-subtitle">Differential gene expression significance vs magnitude</p>
                                    
                                    <div className="gene-volcano-container">
                                        <Plot 
                                            data={[{ 
                                                x: sortedData.diseaseVolcano?.map(d => d.log2FoldChange), 
                                                y: sortedData.diseaseVolcano?.map(d => -Math.log10(d.padj || 1e-300)), 
                                                mode: 'markers', 
                                                type: 'scattergl',
                                                text: sortedData.diseaseVolcano?.map(d => d.gene_id), 
                                                hovertemplate: '<b>%{text}</b><br>Log2FC: %{x:.2f}<br>-Log10(padj): %{y:.2f}<extra></extra>',
                                                marker: { 
                                                    color: sortedData.diseaseVolcano?.map(d => {
                                                        if (d.padj >= 0.05) return 'rgba(156, 163, 175, 0.4)';
                                                        if (d.log2FoldChange > 1) return '#fca5a5';
                                                        if (d.log2FoldChange < -1) return '#7dd3fc';
                                                        return 'rgba(156, 163, 175, 0.6)';
                                                    }),
                                                    size: 4,
                                                    opacity: 0.7,
                                                    line: { 
                                                        color: sortedData.diseaseVolcano?.map(d => {
                                                            if (d.padj >= 0.05) return 'rgba(156, 163, 175, 0.6)';
                                                            if (d.log2FoldChange > 1) return '#ef4444';
                                                            if (d.log2FoldChange < -1) return '#0ea5e9';
                                                            return 'rgba(156, 163, 175, 0.8)';
                                                        }),
                                                        width: 0.5
                                                    }
                                                } 
                                            }]} 
                                            layout={{ 
                                                ...plotLayout,
                                                height: 380,
                                                xaxis: { 
                                                    title: 'Log2 Fold Change', 
                                                    titlefont: { size: 12 },
                                                    zeroline: true,
                                                    zerolinecolor: 'rgba(0,0,0,0.3)',
                                                    zerolinewidth: 1,
                                                    showgrid: true,
                                                    gridcolor: 'rgba(0,0,0,0.05)'
                                                }, 
                                                yaxis: { 
                                                    title: '-Log10(Adjusted P-Value)', 
                                                    titlefont: { size: 12 },
                                                    showgrid: true,
                                                    gridcolor: 'rgba(0,0,0,0.05)'
                                                },
                                                hovermode: 'closest'
                                            }} 
                                            config={plotConfig}
                                            style={{ width: '100%', height: '380px' }}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Regulation Tables */}
                            <div className="gene-regulation-section">
                                <CleanDataTable data={sortedData.diseaseUp} title="Up-regulated Genes" />
                                <CleanDataTable data={sortedData.diseaseDown} title="Down-regulated Genes" />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default GenePage;
